import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@/app/contract';
import { Avatar, Banner, Button, Icon, IconButton, SettingsRow, Spinner, Toggle } from '@/ui';
import type { Account } from '@/model';
import { useMediaUrl } from '@/model';
import './me.css';

export function Page({ title, children, root = false, action }: { title: string; children: ReactNode; root?: boolean; action?: ReactNode }) {
  const nav = useNavigation(); const { t } = useTranslation('common');
  return <section className={'me-page' + (root ? ' me-page--root' : '')} aria-label={title}>
    {!root && <header className="me-page__header">{nav.layout === 'phone' && <IconButton className="me-back" name="chevron.left" label={t('back')} onClick={() => nav.back()} />}<h1>{title}</h1>{action}</header>}
    <div className="me-page__content">{children}</div>
  </section>;
}
export function Footnote({ children }: { children: ReactNode }) { return <p className="me-footnote">{children}</p>; }
export function Group({ children }: { children: ReactNode }) { return <div className="me-group">{children}</div>; }
export function LinkRow({ title, icon, path, value, tint, disabled }: { title: string; icon: string; path: string; value?: string; tint?: string; disabled?: boolean }) {
  const nav = useNavigation();
  const selected = nav.layout !== 'phone' && (window.location.pathname === path || window.location.pathname.startsWith(path + '/'));
  return <SettingsRow title={title} icon={icon} tint={tint} value={value} chevron selected={selected} disabled={disabled} onClick={() => nav.open(path)} />;
}
export function SwitchRow({ title, icon, checked, disabled, onChange, tint }: {
  title: string; icon: string; checked: boolean; disabled?: boolean; onChange: (value: boolean) => void; tint?: string;
}) {
  return <SettingsRow icon={icon} tint={tint} title={title} disabled={disabled}
    control={<Toggle hideLabel label={title} checked={checked} disabled={disabled} onCheckedChange={onChange} />} />;
}
export function Notice({ error, retry, loading }: { error?: unknown; retry?: () => void; loading?: boolean }) {
  const { t } = useTranslation('me');
  if (loading) return <div className="me-loading"><Spinner label={t('common:loading')} /></div>;
  if (!error) return null;
  return <Banner tone="error" title={typeof error === 'string' ? error : t('errors.load')}
    action={retry && <Button size="small" onClick={retry}>{t('actions.tryAgain')}</Button>} />;
}
export function AccountAvatar({ account, size = 44 }: { account: Account; size?: number }) {
  return account.avatar_media_id ? <PhotoAvatar account={account} size={size} mediaId={account.avatar_media_id} />
    : <Avatar name={account.display_name} seed={account.id} size={size} kind={account.kind === 'agent' ? 'agent' : 'person'} />;
}
function PhotoAvatar({ account, size, mediaId }: { account: Account; size: number; mediaId: string }) {
  const media = useMediaUrl(mediaId, 'thumb');
  return <Avatar name={account.display_name} seed={account.id} size={size}
    kind={account.kind === 'agent' ? 'agent' : 'person'} src={media.status === 'ready' ? media.url : undefined} />;
}
export function PasswordToggleIcon({ show }: { show: boolean }) { return <Icon name={show ? 'eye.slash' : 'eye'} />; }

